import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import { toast } from "react-toastify";
import styles from "../../customDashboard.module.css";
import DashboardIcon from "../../../../../assets/icons/dashboard-icon.svg";
import CalendarIcon from "../../../../../assets/icons/calendar.svg";
import EditIcon from "../../../../../assets/icons/edit.svg";
import CircleXMarkIcon from "../../../../../assets/icons/circle-xmark.svg";
import DocumentCirclePlusIcon from "../../../../../assets/icons/document-circle-plus.svg";
import StarOutlineIcon from "../../../../../assets/icons/star-outline.svg";
import StarFilledIcon from "../../../../../assets/icons/star-filled.svg";
import UserIcon from "../../../../../assets/icons/user.svg";
import { setFavoriteDashboard, removeFavoriteDashboard } from "../../../../../services/preferences";
import { readUserById } from "../../../../../services/academetrics";

const DashboardList = ({ 
  dashboardsList, 
  onSelectDashboard, 
  onStartCreating,
  onDeleteDashboard,
  onEditDashboard,
  onCopyDashboard,
  currentUserId,
  favoriteDashboardId,
  onFavoriteChange
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [hoveredFavoriteId, setHoveredFavoriteId] = useState(null);
  const [savingFavorite, setSavingFavorite] = useState(false);
  const [creatorNames, setCreatorNames] = useState({});
  const menuRef = useRef(null);
  const fetchedUserIdsRef = useRef(new Set());

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Buscar nomes dos criadores dos dashboards
  useEffect(() => {
    const fetchCreatorNames = async () => {
      if (!dashboardsList || dashboardsList.length === 0) return;

      const uniqueCreatorIds = [...new Set(
        dashboardsList
          .map(dashboard => dashboard.createdByUserId)
          .filter(Boolean)
      )];

      const namesToFetch = uniqueCreatorIds.filter(
        userId => !fetchedUserIdsRef.current.has(userId)
      );

      if (namesToFetch.length === 0) return;

      // Marcar como buscando para evitar requisições duplicadas
      namesToFetch.forEach(userId => fetchedUserIdsRef.current.add(userId));

      const namesPromises = namesToFetch.map(async (userId) => {
        try {
          const user = await readUserById(userId);
          if (user) {
            const displayName = user.firstName 
              ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`
              : user.email || 'Usuário desconhecido';
            return { userId, displayName };
          }
          return { userId, displayName: 'Usuário desconhecido' };
        } catch (error) {
          console.error(`Erro ao buscar usuário ${userId}:`, error);
          return { userId, displayName: 'Usuário desconhecido' };
        }
      });

      const names = await Promise.all(namesPromises);
      setCreatorNames(prevNames => {
        const newCreatorNames = { ...prevNames };
        names.forEach(({ userId, displayName }) => {
          newCreatorNames[userId] = displayName;
        });
        return newCreatorNames;
      });
    };

    fetchCreatorNames();
  }, [dashboardsList]);

  const handleMenuClick = (e, dashboardId) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === dashboardId ? null : dashboardId);
  };

  const handleOptionClick = (e, action, dashboard) => {
    e.stopPropagation();
    setOpenMenuId(null);
    
    switch (action) {
      case 'delete':
        onDeleteDashboard?.(dashboard);
        break;
      case 'edit':
        onEditDashboard?.(dashboard);
        break;
      case 'copy':
        onCopyDashboard?.(dashboard);
        break;
      default:
        break;
    }
  };

  const handleFavoriteClick = async (e, dashboard) => {
    e.stopPropagation();
    
    if (savingFavorite) return;
    
    const dashboardId = dashboard.dashboardId || dashboard.id;
    const isFavorite = String(favoriteDashboardId) === String(dashboardId);
    
    setSavingFavorite(true);
    
    try {
      if (isFavorite) {
        // Remove o favorito
        await removeFavoriteDashboard();
        onFavoriteChange?.(null);
        toast.success("Dashboard removido dos favoritos");
      } else {
        // Define como favorito
        await setFavoriteDashboard(dashboardId);
        onFavoriteChange?.(String(dashboardId));
        toast.success("Dashboard definido como favorito! Ele será exibido na tela principal.");
      }
    } catch (error) {
      toast.error("Erro ao atualizar favorito. Tente novamente.");
      console.error("Erro ao atualizar favorito:", error);
    } finally {
      setSavingFavorite(false);
    }
  };

  const isOwner = (dashboard) => {
    const ownerId = dashboard.ownerUserId || dashboard.userId;
    return ownerId === currentUserId;
  };

  const isFavorite = (dashboard) => {
    const dashboardId = dashboard.dashboardId || dashboard.id;
    return String(favoriteDashboardId) === String(dashboardId);
  };

  return (
    <div className={styles.dashboardListContent}>
      <div className={styles.dashboardListGrid}>
        {dashboardsList.map((dashboard) => {
          const dashboardId = dashboard.dashboardId || dashboard.id;
          const isMenuOpen = openMenuId === dashboardId;
          const isDashboardFavorite = isFavorite(dashboard);
          const isHovered = hoveredFavoriteId === dashboardId;
          
          return (
            <div 
              key={dashboardId} 
              className={clsx(styles.dashboardListCard, isMenuOpen && styles.dashboardListCardMenuOpen)}
              onClick={() => onSelectDashboard(dashboard)}
            >
              <div className={styles.dashboardListCardHeader}>
                <img src={DashboardIcon} alt="Dashboard" className={styles.dashboardListCardIcon} />
                <div className={styles.dashboardListCardHeaderRight}>
                  {isDashboardFavorite && (
                    <div className={styles.dashboardListCardBadgeFavorite}>Favorito</div>
                  )}
                  <div className={styles.dashboardListCardBadge}>Dashboard</div>
                  
                  {/* Botão de Favorito */}
                  <div 
                    className={styles.favoriteButtonWrapper}
                    onMouseEnter={() => setHoveredFavoriteId(dashboardId)}
                    onMouseLeave={() => setHoveredFavoriteId(null)}
                  >
                    <button
                      className={clsx(
                        styles.favoriteButton,
                        isDashboardFavorite && styles.favoriteButtonActive
                      )}
                      onClick={(e) => handleFavoriteClick(e, dashboard)}
                      disabled={savingFavorite}
                      aria-label={isDashboardFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                    >
                      <img 
                        src={isDashboardFavorite ? StarFilledIcon : StarOutlineIcon} 
                        alt={isDashboardFavorite ? "Favorito" : "Não favorito"}
                        className={styles.favoriteIcon}
                      />
                    </button>
                    
                    {/* Tooltip */}
                    {isHovered && (
                      <div className={styles.favoriteTooltip}>
                        {isDashboardFavorite 
                          ? "Clique para remover dos favoritos" 
                          : "Ao favoritar, este dashboard será exibido na tela principal do Dashboard"}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.dashboardListCardTitleRow}>
                <h3 className={styles.dashboardListCardTitle}>{dashboard.name}</h3>
                <div className={styles.dashboardMenuWrapper} ref={isMenuOpen ? menuRef : null}>
                  <button 
                    className={styles.dashboardMenuButton}
                    onClick={(e) => handleMenuClick(e, dashboardId)}
                    aria-label="Menu de opções"
                  >
                    <span className={styles.dashboardMenuDots}>⋮</span>
                  </button>
                  {isMenuOpen && (
                    <div className={styles.dashboardMenuDropdown}>
                      {isOwner(dashboard) && (
                        <button 
                          className={styles.dashboardMenuItem}
                          onClick={(e) => handleOptionClick(e, 'edit', dashboard)}
                        >
                          <img src={EditIcon} alt="Editar" className={styles.dashboardMenuItemIcon} />
                          Editar dashboard
                        </button>
                      )}
                      {isOwner(dashboard) && !isDashboardFavorite && (
                        <button 
                          className={clsx(styles.dashboardMenuItem, styles.dashboardMenuItemDanger)}
                          onClick={(e) => handleOptionClick(e, 'delete', dashboard)}
                        >
                          <img src={CircleXMarkIcon} alt="Deletar" className={styles.dashboardMenuItemIcon} />
                          Deletar dashboard
                        </button>
                      )}
                      <button 
                        className={styles.dashboardMenuItem}
                        onClick={(e) => handleOptionClick(e, 'copy', dashboard)}
                      >
                        <img src={DocumentCirclePlusIcon} alt="Copiar" className={styles.dashboardMenuItemIcon} />
                        Copiar para mim
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <p className={styles.dashboardListCardDescription}>
                {dashboard.description || "Sem descrição"}
              </p>
              {(dashboard.createdAt || dashboard.createdByUserId) && (
                <div className={styles.dashboardListCardFooter}>
                  {dashboard.createdByUserId && (
                    <span className={styles.dashboardListCardCreator}>
                      <img src={UserIcon} alt="Criador" className={styles.dashboardListCardCreatorIcon} />
                      Criado por: {creatorNames[dashboard.createdByUserId] || 'Carregando...'}
                    </span>
                  )}
                  {dashboard.createdAt && (
                    <span className={styles.dashboardListCardDate}>
                      <img src={CalendarIcon} alt="Data" className={styles.dashboardListCardDateIcon} />
                      Criado em {new Date(dashboard.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div 
          className={clsx(styles.dashboardListCard, styles.createNewCard)}
          onClick={onStartCreating}
        >
          <span className={styles.createNewIcon}>+</span>
          <h3 className={styles.dashboardListCardTitle}>Criar dashboard customizado</h3>
        </div>
      </div>
    </div>
  );
};

export default DashboardList;
